/**
 * Marken-Paket der Oberflaeche (Konzept J.1, erste Stufe).
 *
 * Welche Marke gebaut wird, entscheidet VITE_BRAND zur Bauzeit (siehe
 * src/frontend/Dockerfile, Build-Arg BRAND). Ohne Angabe ist es HVACloud,
 * die Dev-Instanz aendert sich also nicht.
 *
 * Alles Sichtbare, das eine Marke traegt, kommt von hier: Produktname,
 * Name des KI-Assistenten, Logo, Favicon, Standardthema. Die Farben haengen
 * nicht hier, sondern in brands.css an `html[data-brand=...]`, damit die
 * Kaskade sie wie die Tokens behandelt. Der Attributschalter ist mit Absicht
 * ein Laufzeit-Mechanismus: wenn J.1 spaeter online pflegbar wird, kann der
 * Server das Attribut setzen, ohne dass sich an den Komponenten etwas aendert.
 */

export type BrandId = 'hvacloud' | 'austriaemail' | 'boesch';

export interface BrandLogo {
  /** Bildquelle fuer dunkle Flaechen (helle Zeichnung). Leer = eingebautes HVACloud-Zeichen. */
  onDark: string;
  /** Bildquelle fuer helle Flaechen (dunkle Zeichnung). */
  onLight: string;
  /** Seitenverhaeltnis Breite/Hoehe, damit die Seitenleiste vor dem Laden nicht springt. */
  aspect: number;
  /** Hoehe in der ausgeklappten Seitenleiste in px. Fehlt sie, gilt 56. */
  sidebarHeight?: number;
}

export interface Brand {
  id: BrandId;
  /** Name des Produkts: Fenstertitel, aria-Labels, Hilfetexte. */
  productName: string;
  /** Name, unter dem der KI-Assistent auftritt (Dialogtitel, Fortschrittstexte). */
  aiName: string;
  /** Null = das eingebaute HVACloud-SVG wird gezeichnet. */
  logo: BrandLogo | null;
  /** Favicon-Pfade unter /brand/<id>/, null = Vorgabe aus index.html. */
  favicon: { ico: string; png96: string; appleTouch: string } | null;
  /** Thema beim allerersten Start, bevor der Nutzer etwas gewaehlt hat. */
  defaultTheme: 'dark' | 'light';
  /** Dezenter Hinweis "Technik: HVACloud" am Fuss der Seitenleiste. */
  poweredBy: boolean;
}

const BRANDS: Record<BrandId, Brand> = {
  hvacloud: {
    id: 'hvacloud',
    productName: 'HVACloud',
    aiName: 'HVACloud AI',
    logo: null,
    favicon: null,
    defaultTheme: 'dark',
    poweredBy: false,
  },
  austriaemail: {
    id: 'austriaemail',
    productName: 'Austria Email HVACloud',
    aiName: 'HVACloud AI',
    logo: {
      onDark: '/brand/austriaemail/logo-on-dark.png',
      onLight: '/brand/austriaemail/logo-on-light.png',
      aspect: 250 / 173,
    },
    favicon: {
      ico: '/brand/austriaemail/favicon.ico',
      png96: '/brand/austriaemail/favicon-96x96.png',
      appleTouch: '/brand/austriaemail/apple-touch-icon.png',
    },
    defaultTheme: 'light',
    poweredBy: true,
  },
  // Instanz boesch.hvacloud.at (die Dev-Instanz mit den Bösch-Panels).
  // Logo aus customer-projects/Bösch, Zeichen auf dunklen Flaechen hell
  // umgefaerbt; Farbquadrate bleiben.
  boesch: {
    id: 'boesch',
    productName: 'Bösch HVACloud',
    aiName: 'HVACloud AI',
    logo: {
      // -2: die Dateien gehen ohne Cache-Control hinaus, ein gleicher Name blieb
      // tagelang alt im Browser. Die alten Namen bleiben fuer Mails liegen.
      onDark: '/brand/boesch/logo-on-dark-2.png',
      onLight: '/brand/boesch/logo-on-light-2.png',
      aspect: 886 / 262,
      // Breiter Schriftzug ohne Unterzeile: bei 56 px fuellte er die Leiste (Patrick, 29.10.2026).
      sidebarHeight: 34,
    },
    favicon: {
      ico: '/brand/boesch/favicon.ico',
      png96: '/brand/boesch/favicon-96x96.png',
      appleTouch: '/brand/boesch/apple-touch-icon.png',
    },
    defaultTheme: 'light',
    poweredBy: true,
  },
};

function resolveBrandId(): BrandId {
  const raw = (import.meta.env.VITE_BRAND ?? 'hvacloud').toString().trim().toLowerCase();
  return raw in BRANDS ? (raw as BrandId) : 'hvacloud';
}

export const BRAND: Brand = BRANDS[resolveBrandId()];

/**
 * Setzt Attribut, Titel und Favicon beim Start. Muss vor dem ersten Render
 * laufen und vor theme.ts, weil das Standardthema der Marke dort als
 * Rueckfallwert gelesen wird.
 */
export function applyBrandToDocument(): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.setAttribute('data-brand', BRAND.id);
  document.title = BRAND.productName;
  // HVACloud selbst als App installierbar, mit Start auf dem Dashboard. Die
  // Web-HMI (/m) tauscht dieses Manifest gegen ihr eigenes (WebHmiPage); so
  // installiert man aus dem Dashboard das Dashboard und aus /m die Web-HMI.
  if (!document.querySelector('link[rel="manifest"]')) {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `/brand/${BRAND.id}/dashboard.manifest.json`;
    link.dataset.app = 'dashboard';
    document.head.appendChild(link);
  }

  if (!BRAND.favicon) return;
  const head = document.head;
  for (const link of Array.from(head.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]'))) {
    link.remove();
  }
  const add = (rel: string, href: string, type?: string, sizes?: string) => {
    const el = document.createElement('link');
    el.rel = rel;
    el.href = href;
    if (type) el.type = type;
    if (sizes) el.setAttribute('sizes', sizes);
    head.appendChild(el);
  };
  add('icon', BRAND.favicon.ico, 'image/x-icon');
  add('icon', BRAND.favicon.png96, 'image/png', '96x96');
  add('apple-touch-icon', BRAND.favicon.appleTouch, undefined, '180x180');
}
